"use client"

import Navbar from "@/components/landing/navbar";
import Footer from "@/components/landing/footer";
import { defaultCountryConfig } from "@/config/counties/default";
import { LEGAL_UPDATED } from "@/lib/content-dates";

// One source for the date shown here and the `lastmod` the sitemap sends
// Google (lib/content-dates.ts). They used to disagree by over a year: this
// page read "January 5, 2025" — literal brackets and all, and predating the
// domain's own 2026 registration — while the sitemap reported 2026-05-01.
const LAST_UPDATED = LEGAL_UPDATED.toLocaleDateString("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
  timeZone: "UTC",
});

export default function PrivacyPageContent() {
  return (
    <div className="min-h-screen bg-card">
      <Navbar data={defaultCountryConfig.navbar} />
      <div className="pt-16">
        <div className="container mx-auto px-4 py-8 sm:py-12 md:py-16 max-w-4xl">
          {/* Header Section */}
          <div className="mb-12 pb-8 border-b-2 border-primary/20">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ink mb-4 tracking-tight">
              Privacy Policy
            </h1>
            <div className="h-1 w-24 bg-primary rounded-full mb-6"></div>
            <div className="text-sm text-muted-ink">
              <p><strong className="text-body">Last updated:</strong> {LAST_UPDATED}</p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none text-body space-y-8 leading-relaxed">
            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                Welcome!
              </h2>
              <p className="leading-relaxed">
                This Privacy Policy ("Policy") describes how we collect, process, share, and safeguard Personal Data we collect from you, or that you provide to us, in connection with your use of our website https://www.leadsminer.app/ ("Website") and in connection with the provision of any Services through the Website, including services that enable users to purchase and access business leads (collectively, the "Services").
              </p>
              <p className="leading-relaxed">
                This Policy also applies to Personal Data of individuals whose business information may appear in our Services. It explains your rights and choices with respect to your Personal Data and how you can contact us if you have questions or concerns.
              </p>
              <p className="leading-relaxed">
                In this Policy, "Personal Data" or "Personal Information" means any information relating to an identified or identifiable individual.
              </p>
              <p><strong className="text-ink">Please read this Policy carefully.</strong></p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-ink mb-6 pb-2 border-b border-line">
                Personal Data We Collect
              </h2>
              
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-ink mt-6 mb-4 text-primary/90">1. Personal Data You Provide to Us</h3>
              <p className="leading-relaxed">We collect the following Personal Data when you provide it:</p>
              
              <h4 className="text-base sm:text-lg font-semibold text-ink mt-6 mb-3">Account Information.</h4>
              <p className="leading-relaxed">
                When you create an account to use our Services, we collect information necessary to set up and maintain your account, including your name, email address, password, phone number, company name, and other account details.
              </p>
              
              <h4 className="text-base sm:text-lg font-semibold text-ink mt-6 mb-3">Contact Information.</h4>
              <p className="leading-relaxed">
                If you contact us by email or through forms on the Website, you may provide us with your name, email, phone number, and other details that you choose to submit.
              </p>
              
              <h4 className="text-base sm:text-lg font-semibold text-ink mt-6 mb-3">Support Information.</h4>
              <p className="leading-relaxed">
                If you request support, we collect the information you provide, including your contact details and information about the reason for your support request.
              </p>
              
              <h4 className="text-base sm:text-lg font-semibold text-ink mt-6 mb-3">Financial Information.</h4>
              <p className="leading-relaxed">
                To purchase subscription services or credits on our Website, you will provide payment information. We do not store your full credit card data; instead we rely on third‑party payment processors to handle payment transactions.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-ink mt-6 mb-4 text-primary/90">2. Information Collected via Automated Means</h3>
              <p className="leading-relaxed">We also collect Personal Data automatically when you use our Services:</p>
              
              <h4 className="text-base sm:text-lg font-semibold text-ink mt-6 mb-3">Cookies and Similar Technologies.</h4>
              <p className="leading-relaxed">
                We use cookies, pixel tags, and similar technologies to collect information about your interaction with our Website. For more information on our use of cookies, see the Cookie Policy.
              </p>
              
              <h4 className="text-base sm:text-lg font-semibold text-ink mt-6 mb-3">Device and Usage Information.</h4>
              <p className="leading-relaxed">
                We collect data such as your IP address, browser type, device identifiers, operating system, usage patterns, date/time stamps, and interactions with the Website.
              </p>
              
              <h4 className="text-base sm:text-lg font-semibold text-ink mt-6 mb-3">Single Sign‑On.</h4>
              <p className="leading-relaxed">
                If you choose to log in or connect via third‑party services (e.g., Google, Facebook), we may receive Personal Data from those providers as permitted by their terms.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-ink mt-6 mb-4 text-primary/90">3. Information From Third Parties and Other Sources</h3>
              <p className="leading-relaxed">We may also collect Personal Data from external sources:</p>
              
              <h4 className="text-base sm:text-lg font-semibold text-ink mt-6 mb-3">Publicly Available Information.</h4>
              <p className="leading-relaxed">
                We may collect business information from public sources such as corporate websites, public records, and other publicly accessible resources.
              </p>
              
              <h4 className="text-base sm:text-lg font-semibold text-ink mt-6 mb-3">Data Providers.</h4>
              <p className="leading-relaxed">
                We may collect data from third‑party data providers or partners that offer business contact information.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-ink mb-6 pb-2 border-b border-line">
                How We Use Personal Data
              </h2>
              <p className="leading-relaxed">We use the Personal Data we collect for the following purposes:</p>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">1. To Operate and Administer the Services</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Create and maintain your account.</li>
                <li>Provide, maintain, and support the Services, including the business lead database.</li>
                <li>Communicate with you for customer service and administrative purposes.</li>
              </ul>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">2. To Improve, Monitor, Personalize, and Protect Our Services</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Analyze usage to improve our Website and Services.</li>
                <li>Provide personalized content or features.</li>
                <li>Protect against fraud or misuse of our Services.</li>
                <li>Aggregate or anonymize data for analytics and reporting.</li>
              </ul>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">3. For Marketing and Advertising</h3>
              <p className="leading-relaxed">Subject to applicable laws:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We may send you marketing communications (you may opt‑out).</li>
                <li>We may use cookies and tracking technologies to show you ads on other websites.</li>
              </ul>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">4. For Administrative and Legal Purposes</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Comply with legal obligations, enforce our agreements, and protect our rights and the rights of others.</li>
              </ul>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-ink mb-6 pb-2 border-b border-line">
                Who We Share Personal Data With
              </h2>
              <p className="leading-relaxed">We may disclose Personal Data in the following circumstances:</p>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">1. With Customers and Users</h3>
              <p className="leading-relaxed">
                We may share business lead data (e.g., names, business contact details) with users and customers who access these leads through the Services.
              </p>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">2. With Affiliates and Group Entities</h3>
              <p className="leading-relaxed">
                We may share Personal Data with our affiliates or business units.
              </p>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">3. With Service Providers</h3>
              <p className="leading-relaxed">
                We share Personal Data with third parties that help us provide services (e.g., payment processors, hosting providers, support platforms).
              </p>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">4. For Legal Reasons</h3>
              <p className="leading-relaxed">
                We may disclose Personal Data to comply with legal obligations or to protect rights, property, or safety.
              </p>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">5. In Corporate Transactions</h3>
              <p className="leading-relaxed">
                If LeadsMiner is involved in a merger, acquisition, bankruptcy, or sale of assets, Personal Data may be transferred.
              </p>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">6. Aggregated Information</h3>
              <p className="leading-relaxed">
                We may share anonymized or aggregated data for analytics or business purposes.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-ink mb-6 pb-2 border-b border-line">
                Your Choices About Your Personal Data
              </h2>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">Account Controls</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>You may update, correct, or delete your Personal Data by accessing your account or contacting us.</li>
              </ul>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">Opt‑Out of Marketing</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>You may opt‑out of marketing emails by using the unsubscribe link in the email or contacting us.</li>
              </ul>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">Access, Deletion, and Correction Requests</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>You may request access to, correction of, or deletion of your Personal Data by contacting us.</li>
                <li>We may require identity verification prior to fulfilling certain requests.</li>
              </ul>
              
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">Opt‑Out of Targeted Advertising</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>You may opt‑out of targeted advertising by using browser or device settings or privacy controls where available.</li>
              </ul>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                International Data Transfers
              </h2>
              <p className="leading-relaxed">
                LeadsMiner is based in the United States. Your Personal Data may be transferred to, stored in, and processed in the U.S. or other countries with different data protection laws. We use appropriate safeguards as required by applicable law.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                Children's Privacy
              </h2>
              <p className="leading-relaxed">
                Our Services are not directed at individuals under 18. We do not knowingly collect Personal Data from children under 18.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                Data Security
              </h2>
              <p className="leading-relaxed">
                We implement reasonable technical, physical, and administrative safeguards to protect Personal Data. However, no system is completely secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                Data Retention
              </h2>
              <p className="leading-relaxed">
                We retain Personal Data as necessary to fulfill the purposes outlined in this Policy, comply with legal obligations, resolve disputes, and enforce agreements.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                Third‑Party Services
              </h2>
              <p className="leading-relaxed">
                Our Website may include links to third‑party services. We are not responsible for the privacy practices of third parties. You should review their privacy policies before providing your information.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                State Specific Disclosures
              </h2>
              <p className="leading-relaxed">
                To the extent required by applicable law (e.g., CCPA/CPRA), you may have specific rights related to your Personal Data, including the right to access, delete, or opt‑out of certain processing. You may exercise these rights by contacting us.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                Changes to this Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Policy from time to time. We will post the updated Policy with a new "Last Updated" date. If we make material changes, we may notify you by email or through the Services.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                Contact Us
              </h2>
              <p className="leading-relaxed">If you have any questions about this Policy or your Personal Data, contact us at:</p>
              <div className="mt-4 p-4 bg-surface-alt rounded-lg border border-line">
                <p className="font-semibold text-ink mb-1">LeadsMiner</p>
                <p className="text-muted-ink mb-1">
                  Email:{" "}
                  <a href="mailto:support@leadsminer.app" className="text-primary hover:underline">
                    support@leadsminer.app
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer data={defaultCountryConfig.footer} />
    </div>
  );
}
